/*
 *
 * Uses of derived variables:
 * - as conditions for applicability of a question in a given survey (type = 'boolean', with getUserValue defined)
 * - for display as part of a question in a survey (a calculated, read-only response)
 * - for use in filtering results
 */

import concat from 'lodash/concat';
import toNumber from 'lodash/toNumber';
import Api from '@/providers/Api.js';

const DerivedVariables = {
  // ---------- variable definitions ----------

  // user-level variables used for filtering results - values of these are only looked up on the server side

  userFilterVariables: {
    // userAge
    // userDOB

    userSpecialtyId: {
      name: 'userSpecialtyId',
      title: 'Specialty',
    },

    userSubSpecialtyId: {
      name: 'userSubSpecialtyId',
      title: 'Subspecialty',
    },

    userZipcode: {
      name: 'userZipcode',
      title: 'Location',
    },

    userGender: {
      name: 'userGender',
      title: 'Gender Identity',
    },

    userEthnicity: {
      name: 'userEthnicity',
      title: 'Ethnicity',
    },

    userVisaStatus: {
      name: 'userVisaStatus',
      title: 'Visa Status',
    },

    userUSVeteran: {
      name: 'userUSVeteran',
      title: 'US Veteran',
    },
  },

  // survey-level variables used to display calculated values - can be used in results, but not result filters

  calculationVariables: {
    totalCompensation: {
      name: 'totalCompensation',
      prefix: '$',
      title: 'Total Compensation',
      requiredVariableIds: getTotalCompensationVariableIds(),
      getSurveyValue: (variables) => {
        return getTotalCompensation(variables);
      },
    },
    dollarsPerWrvu: {
      name: 'dollarsPerWrvu',
      prefix: '$',
      title: 'Compensation per wRVU',
      requiredVariableIds: concat(getTotalCompensationVariableIds(), 112),
      getSurveyValue: (variables) => {
        const totalComp = getTotalCompensation(variables);
        const wrvuCount = getValue(variables, 112);
        // console.log(totalComp, wrvuCount)
        if (totalComp > 0 && wrvuCount > 0) {
          return totalComp / wrvuCount;
        } else {
          return null;
        }
      },
    },
  },

  // ---------- public utility methods ----------

  // called prior to saving survey responses
  updateResponseValues(variables) {
    // console.log('urv')
    variables.forEach((v) => {
      if (v.dv_name) {
        const dv = Object.values(DerivedVariables.calculationVariables).find(
          (dv) => dv.name == v.dv_name
        );
        if (dv) {
          v.response.value = dv.getSurveyValue(variables);
          // console.log(dv.name, v.response.value)
        }
      }
    });
  },
};

// ---------- intermediate calculations ----------

function getTotalCompensationVariableIds() {
  return [
    99, // Administrative compensation

    111, // Research compensation

    116, // Retirement benefits
    117, // Health Insurance benefits
    118, // Disability Insurance benefits
    119, // Life Insurance benefits
    120, // Professional dues benefits
    121, // Licensing fees benefits
    122, // CME stipend benefits

    123, // Total cash compensation

    124, // Consulting Revenue
    125, // Royalty Revenue
    126, // Authorship Revenue
    127, // IP Revenue
    128, // Tuition Payment Revenue
    129, // Other Revenue
  ];
}

function getTotalCompensation(variables) {
  const sv = sumValues(variables, getTotalCompensationVariableIds());
  // console.log('sum', sv)
  return sv;
}

// ---------- intermediate calculations ----------

function getValue(variables, vid) {
  const variable = variables.find((v) => v.id == vid);
  if (variable && variable.response) {
    let val = variable.response.value;
    // val = Api.stripSeparators(val)
    return toNumber(val);
  } else {
    return null;
  }
}

// ---------- private utility methods ----------

function sumValues(variables, vids) {
  let sum = 0;
  vids.forEach((vid) => {
    const variable = variables.find((v) => v.id == vid);
    let val = variable.response
      ? toNumber(Api.stripSeparators(variable.response.value))
      : 0;
    // console.log('val', vid, val)
    sum += val;
    // console.log('newSum', sum)
  });
  return sum;
}

export default DerivedVariables;
